import { classesData } from "./../../../components/school/ClassesSchool/tables/ClassesTable";
import { create } from "domain";
import { fr } from "yup-locales";
import { CompetenceStudent } from "./../competenceStudent/competenceStudent.type";
/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  addDoc,
  where,
  serverTimestamp,
  getDocs,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
  arrayRemove,
  arrayUnion,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { onAuthStateChanged, updateProfile } from "firebase/auth";
import { User, StudentDashboardStats } from "./user.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addUser,
  manageUser,
  addStudentAndParent,
  handleResendAccess,
  editUserData,
  editStudentAndParent,
  importStudents,
  importTeachers,
  changeStudentTutor,
} from "../../../services/firebaseService";
import { ObjectifStudent } from "../objectifStudent/objectifStudent.type";
import { DevoirStudent } from "../devoirStudent/devoirStudent.type";
import { Absence } from "../absence/absence.type";
import { Devoir } from "../devoir/devoir.type";
import { Objectif } from "../objectif/objectif.type";
import { Competence } from "../competence/competence.type";
import { Matiere } from "../matiere/matiere.type";
import { Evaluation } from "../evaluations/evaluation.type";
import { Observation } from "../observation/observation.type";

export const UserApi = createApi({
  reducerPath: "userApi",
  tagTypes: [
    "user",
    "admins",
    "users",
    "userId",
    "profs",
    "students",
    "resendAccess",
    "dashboardStudent",
  ],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      queryFn: async () => {
        try {
          const user: any = await new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              unsubscribe();
              resolve(user);
            });
          });

          if (!user) {
            return {
              error: {
                status: "UNAUTHORIZED",
                error: "Utilisateur non connecté",
              },
            };
          }

          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
              data: {
                uid: user.uid,
                email: user.email,
                displayName: userData.fullName,
                photoURL: user.photoURL,
                ...userData,
              },
            };
          } else {
            return {
              error: {
                status: "NOT_FOUND",
                error: "Données utilisateur non trouvées dans Firestore",
              },
            };
          }
        } catch (error) {
          let err = error as any;
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
      providesTags: ["user"],
    }),
    updateUserInfo: builder.mutation({
      queryFn: async (data) => {
        const user = auth.currentUser;
        if (!user) return { error: { message: "Utilisateur non connecté" } };

        try {
          // Mettre à jour Firebase Auth
          await updateProfile(user, { displayName: data?.fullName });

          // Mettre à jour Firestore
          const userRef = doc(db, "users", user.uid);
          await updateDoc(userRef, data);

          return { data: { message: "Mise à jour réussie" } };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["user"],
    }),
    updateUserPhotoURL: builder.mutation({
      queryFn: async (data) => {
        const user = auth.currentUser;
        if (!user) return { error: { message: "Utilisateur non connecté" } };

        try {
          // Mettre à jour Firebase Auth
          await updateProfile(user, { photoURL: data?.photoURL });

          // Mettre à jour Firestore
          const userRef = doc(db, "users", user.uid);
          await updateDoc(userRef, data);

          return { data: { message: "Mise à jour réussie" } };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["user"],
    }),
    addOrEditUser: builder.mutation({
      async queryFn({ uid, ...data }) {
        try {
          if (!uid) {
            const response = await addUser(data);
            return {
              data: response,
              message: "Utilisateur ajouté.",
            };
          } else {
            const response = editUserData(uid, data)
            return {
              data: {
                response: response,
                message:
                  "Informations utilisateur modifiées.",
              },
            };
          }
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "userId", "students"],
    }),
    addOrEditStudentAndParent: builder.mutation({
      async queryFn({ uid, ...data }) {
        try {
          if (!uid) {
            const response = await addStudentAndParent(data);
            return {
              data: response,
              message: "Élève créé.",
            };
          } else {
            // Mettre à jour Firestore
            const response = await editStudentAndParent(uid, data)

            return {
              data: {
                response: response,
                message: "Informations enregistrées.",
              },
            };
          }
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "userId"],
    }),
    changeStudentTutor: builder.mutation({
      async queryFn(data: {
        studentId: string;
        existingParentId?: string;
        newParent?: {
          firstName: string;
          lastName: string;
          email: string;
          phone?: string;
          residence?: string;
          address?: string;
        };
      }) {
        try {
          const response = await changeStudentTutor(data);
          return {
            data: {
              ...(response as any),
              message: (response as any)?.message || "Tuteur changé avec succès.",
            },
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "userId", "students"],
    }),
    resendAccess: builder.mutation({
      async queryFn({ email, fullName }) {
        try {
          const response = await handleResendAccess({ email, fullName });
          return {
            data: response,
            message: "Identifiants envoyés.",
          };
        } catch (error: any) {
          return {
            error: {
              status: "CUSTOM",
              message: "Echec de l’envoi. Veuillez réessayer. ",
            },
          };
        }
      },
      invalidatesTags: ["resendAccess"],
    }),
    getUsers: builder.query({
      async queryFn() {
        try {
          return new Promise((resolve) => {
            const q = query(
              collection(db, "users"),
              where("userType", "not-in", ["admin", "school"]),
              orderBy("createdAt", "desc"),
            );
            onSnapshot(q, (snapshot) => {
              const users = snapshot.docs.map((snap) => ({
                uid: snap.id,
                ...snap.data(),
              }));
              resolve({ data: users as User[] });
            });
            // unsubscribe();
          });
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const usersRef = query(
          collection(db, "users"),
          where("userType", "not-in", ["admin", "school"]),
          orderBy("createdAt", "desc"),
        );
        let unsubscribe;
        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(usersRef, (snapshot) => {
            const users = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            }));
            updateCachedData(() => users as User[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["users"],
    }),
    getAdmins: builder.query({
      async queryFn() {
        try {
          return new Promise((resolve) => {
            const q = query(
              collection(db, "users"),
              where("userType", "==", "admin"),
            );
            onSnapshot(q, (snapshot) => {
              const users = snapshot.docs.map((snap) => ({
                uid: snap.id,
                ...snap.data(),
              }));
              resolve({ data: users });
            });
            // unsubscribe();
          });
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const usersRef = query(
          collection(db, "users"),
          where("userType", "==", "admin"),
        );
        let unsubscribe;
        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(usersRef, (snapshot) => {
            const users = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            }));
            updateCachedData(() => users);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["admins"],
    }),
    // Professeurs rattachés à l'établissement CONNECTÉ uniquement (ceux que
    // cet établissement a lui-même créés, cf. createUserByAdmin ->
    // createdBy = uid de l'établissement) - sans ce filtre, un
    // établissement voyait tous les professeurs de la plateforme, y
    // compris ceux d'autres établissements.
    getProfs: builder.query({
      async queryFn() {
        try {
          const currentUser = auth.currentUser;
          if (!currentUser) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }
          return new Promise((resolve) => {
            const q = query(
              collection(db, "users"),
              where("userType", "==", "teacher"),
              where("createdBy", "==", currentUser.uid),
            );
            onSnapshot(q, (snapshot) => {
              const users = snapshot.docs.map((snap) => ({
                uid: snap.id,
                ...snap.data(),
              })).filter((user: any) => user.archived !== true);
              resolve({ data: users });
            });
            // unsubscribe();
          });
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const usersRef = query(
          collection(db, "users"),
          where("userType", "==", "teacher"),
          where("createdBy", "==", currentUser.uid),
        );
        let unsubscribe;
        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(usersRef, (snapshot) => {
            const users = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            })).filter((user: any) => user.archived !== true);
            updateCachedData(() => users);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["profs"],
    }),
    getArchiveProfs: builder.query({
      async queryFn() {
        try {
          const currentUser = auth.currentUser;
          if (!currentUser) return { error: { message: "Aucun utilisateur connecté." } };
          return new Promise((resolve, reject) => {
            const profsRef = query(
              collection(db, "users"),
              where("userType", "==", "teacher"),
              where("createdBy", "==", currentUser.uid),
              where("archived", "==", true),
            );
            const unsubscribe = onSnapshot(
              profsRef,
              (snapshot) => {
                resolve({
                  data: snapshot.docs.map((snap) => ({
                    uid: snap.id,
                    ...snap.data(),
                  })),
                });
                unsubscribe();
              },
              reject,
            );
          });
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const profsRef = query(
          collection(db, "users"),
          where("userType", "==", "teacher"),
          where("createdBy", "==", currentUser.uid),
          where("archived", "==", true),
        );
        let unsubscribe: (() => void) | undefined;
        try {
          await cacheDataLoaded;
          unsubscribe = onSnapshot(profsRef, (snapshot) => {
            updateCachedData(() =>
              snapshot.docs.map((snap) => ({
                uid: snap.id,
                ...snap.data(),
              })),
            );
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }
        await cacheEntryRemoved;
        unsubscribe?.();
      },
      providesTags: ["profs"],
    }),
    getStudentsByProf: builder.query({
      async queryFn(profId: string) {
        if (!profId) return { error: { message: "profId manquant" } };

        try {
          return new Promise((resolve, reject) => {
            const q = query(
              collection(db, "users"),
              where("userType", "==", "student"),
              where("createdBy", "==", profId),
              where("archived", "!=", true),
            );

            const unsubscribe = onSnapshot(
              q,
              async (snapshot) => {
                const students = snapshot.docs.map((snap) => ({
                  uid: snap.id,
                  ...snap.data(),
                })) as any[];

                // Récupérer les objets classroom pour chaque student
                const studentsWithClassroom = await Promise.all(
                  students.map(async (student) => {
                    if (!student.classe) return student; // pas de classe assignée

                    try {
                      const classroomRef = doc(
                        db,
                        "classrooms",
                        student.classe,
                      ); // adapte le nom de ta collection
                      const classroomSnap = await getDoc(classroomRef);

                      return {
                        ...student,
                        classe: classroomSnap.exists()
                          ? { uid: classroomSnap.id, ...classroomSnap.data() }
                          : student.classe, // fallback à l'id si non trouvé
                      };
                    } catch {
                      return student; // fallback en cas d'erreur
                    }
                  }),
                );

                // Récupérer le parent pour chaque student
                const studentsWithParent = await Promise.all(
                  studentsWithClassroom.map(async (student) => {
                    if (!student.parentId) return student; // pas de classe assignée

                    try {
                      const userRef = doc(db, "users", student.parentId); // adapte le nom de ta collection
                      const userSnap = await getDoc(userRef);

                      return {
                        ...student,
                        parent: userSnap.exists()
                          ? { uid: userSnap.id, ...userSnap.data() }
                          : student.parentId, // fallback à l'id si non trouvé
                      };
                    } catch {
                      return student; // fallback en cas d'erreur
                    }
                  }),
                );

                resolve({ data: studentsWithParent });
                unsubscribe();
              },
              reject,
            );
          });
        } catch (error) {
          return { error };
        }
      },

      keepUnusedDataFor: 0,

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const usersRef = query(
          collection(db, "users"),
          where("userType", "==", "student"),
          where("createdBy", "==", arg),
          where("archived", "!=", true),
        );

        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(usersRef, async (snapshot) => {
            const students = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            })) as any[];

            //Même logique de jointure dans onCacheEntryAdded
            const studentsWithClassroom = await Promise.all(
              students.map(async (student) => {
                if (!student.classe) return student;

                try {
                  const classroomRef = doc(db, "classrooms", student.classe);
                  const classroomSnap = await getDoc(classroomRef);

                  return {
                    ...student,
                    classe: classroomSnap.exists()
                      ? { uid: classroomSnap.id, ...classroomSnap.data() }
                      : student.classe,
                  };
                } catch {
                  return student;
                }
              }),
            );

            // Récupérer le parent pour chaque student
            const studentsWithParent = await Promise.all(
              studentsWithClassroom.map(async (student) => {
                if (!student.parentId) return student; // pas de classe assignée

                try {
                  const userRef = doc(db, "users", student.parentId); // adapte le nom de ta collection
                  const userSnap = await getDoc(userRef);

                  return {
                    ...student,
                    parent: userSnap.exists()
                      ? { uid: userSnap.id, ...userSnap.data() }
                      : student.parentId, // fallback à l'id si non trouvé
                  };
                } catch {
                  return student; // fallback en cas d'erreur
                }
              }),
            );

            updateCachedData(() => studentsWithParent);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },

      providesTags: ["students"],
    }),
    getStudentsAndParentsByProf: builder.query({
      async queryFn(profId: string) {
        if (!profId) return { error: { message: "profId manquant" } };

        try {
          return new Promise((resolve, reject) => {
            const q = query(
              collection(db, "users"),
              where("userType", "==", "student"),
              where("createdBy", "==", profId),
              where("archived", "==", false),
            );

            const unsubscribe = onSnapshot(
              q,
              async (snapshot) => {
                const students = snapshot.docs.map((snap) => ({
                  uid: snap.id,
                  ...snap.data(),
                })) as any[];

                const studentsWithClassroom = await Promise.all(
                  students.map(async (student) => {
                    let classeData = student.classe;
                    if (student.classe) {
                      try {
                        const classroomRef = doc(
                          db,
                          "classrooms",
                          student.classe,
                        );
                        const classroomSnap = await getDoc(classroomRef);
                        if (classroomSnap.exists()) {
                          classeData = {
                            uid: classroomSnap.id,
                            ...classroomSnap.data(),
                          };
                        }
                      } catch (error) {
                        return { error };
                      }
                    }
                    return { ...student, classe: classeData };
                  }),
                );

                const parentIds = Array.from(
                  new Set(
                    studentsWithClassroom
                      .map((s) => s.parentId)
                      .filter(Boolean),
                  ),
                );

                const parents = (
                  await Promise.all(
                    parentIds.map(async (parentId: string) => {
                      try {
                        const parentRef = doc(db, "users", parentId);
                        const parentSnap = await getDoc(parentRef);
                        if (!parentSnap.exists()) return null;
                        //
                        const children = studentsWithClassroom.find(
                          (s) => s.parentId === parentId,
                        );

                        return {
                          uid: parentSnap.id,
                          ...parentSnap.data(),
                          children,
                        };
                      } catch {
                        return null;
                      }
                    }),
                  )
                ).filter(Boolean);

                const users = [...studentsWithClassroom, ...parents];
                resolve({ data: { users } });
                unsubscribe();
              },
              reject,
            );
          });
        } catch (error) {
          return { error };
        }
      },

      keepUnusedDataFor: 0,

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const usersRef = query(
          collection(db, "users"),
          where("userType", "==", "student"),
          where("createdBy", "==", arg),
          where("archived", "==", false),
        );

        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(usersRef, async (snapshot) => {
            const students = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            })) as any[];

            const studentsWithClassroom = await Promise.all(
              students.map(async (student) => {
                let classeData = student.classe;
                if (student.classe) {
                  try {
                    const classroomRef = doc(db, "classrooms", student.classe);
                    const classroomSnap = await getDoc(classroomRef);
                    if (classroomSnap.exists()) {
                      classeData = {
                        uid: classroomSnap.id,
                        ...classroomSnap.data(),
                      };
                    }
                  } catch (error) {
                    return { error };
                  }
                }
                return { ...student, classe: classeData };
              }),
            );

            const parentIds = Array.from(
              new Set(
                studentsWithClassroom.map((s) => s.parentId).filter(Boolean),
              ),
            );

            const parents = (
              await Promise.all(
                parentIds.map(async (parentId: string) => {
                  try {
                    const parentRef = doc(db, "users", parentId);
                    const parentSnap = await getDoc(parentRef);
                    if (!parentSnap.exists()) return null;
                    //
                    const children = studentsWithClassroom.find(
                      (s) => s.parentId === parentId,
                    );

                    return {
                      uid: parentSnap.id,
                      ...parentSnap.data(),
                      children,
                    };
                  } catch {
                    return null;
                  }
                }),
              )
            ).filter(Boolean);

            const users = [...studentsWithClassroom, ...parents];
            updateCachedData(() => ({ users }));
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },

      providesTags: ["students"],
    }),
    getProfsByStudent: builder.query({
      async queryFn(studentId: string) {
        if (!studentId) return { error: { message: "studentId manquant" } };

        try {
          return new Promise((resolve, reject) => {
            const studentRef = doc(db, "users", studentId);

            const unsubscribe = onSnapshot(
              studentRef,
              async (studentSnap) => {
                if (!studentSnap.exists()) {
                  resolve({ error: { message: "Élève introuvable" } });
                  unsubscribe();
                  return;
                }

                const student = {
                  uid: studentSnap.id,
                  ...studentSnap.data(),
                } as any;

                // --- Récupérer le prof
                let profData = null;
                if (student.createdBy) {
                  try {
                    const profRef = doc(db, "users", student.createdBy);
                    const profSnap = await getDoc(profRef);
                    if (profSnap.exists()) {
                      profData = { uid: profSnap.id, ...profSnap.data() };
                    }
                  } catch (error) {
                    return { error };
                  }
                }

                // --- Récupérer le parent ---
                let parentData = null;
                if (student.parentId) {
                  try {
                    const parentRef = doc(db, "users", student.parentId);
                    const parentSnap = await getDoc(parentRef);
                    if (parentSnap.exists()) {
                      parentData = { uid: parentSnap.id, ...parentSnap.data() };
                    }
                  } catch (error) {
                    return { error };
                  }
                }

                const users = [
                  ...(profData ? [profData] : []),
                  ...(parentData ? [parentData] : []),
                ];

                resolve({ data: { users } });
                unsubscribe();
              },
              reject,
            );
          });
        } catch (error) {
          return { error };
        }
      },

      keepUnusedDataFor: 0,

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const studentRef = doc(db, "users", arg);
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(studentRef, async (studentSnap) => {
            if (!studentSnap.exists()) return;

            const student = {
              uid: studentSnap.id,
              ...studentSnap.data(),
            } as any;

            // --- Récupérer le prof
            let profData = null;
            if (student.createdBy) {
              try {
                const profRef = doc(db, "users", student.createdBy);
                const profSnap = await getDoc(profRef);
                if (profSnap.exists()) {
                  profData = { uid: profSnap.id, ...profSnap.data() };
                }
              } catch (error) {
                return { error };
              }
            }

            // --- Récupérer le parent ---
            let parentData = null;
            if (student.parentId) {
              try {
                const parentRef = doc(db, "users", student.parentId);
                const parentSnap = await getDoc(parentRef);
                if (parentSnap.exists()) {
                  parentData = { uid: parentSnap.id, ...parentSnap.data() };
                }
              } catch (error) {
                return { error };
              }
            }

            const users = [
              ...(profData ? [profData] : []),
              ...(parentData ? [parentData] : []),
            ];

            updateCachedData(() => ({ users }));
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },

      providesTags: ["students"],
    }),

    getUserById: builder.query({
      queryFn: async (userId) => {
        try {
          const userRef = doc(db, "users", userId);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            return {
              error: { status: 404, message: "Utilisateur introuvable !" },
            };
          }

          const userData = { uid: userSnap.id, ...userSnap.data() } as any;

          // Récupérer l'objet classroom si l'utilisateur a une classe
          if (userData.classe) {
            const classroomRef = doc(db, "classrooms", userData.classe);
            const classroomSnap = await getDoc(classroomRef);

            if (classroomSnap.exists()) {
              userData.classe = {
                uid: classroomSnap.id,
                ...classroomSnap.data(),
              };
            }
          }

          // Récupérer les professeurs réellement rattachés à l'élève.
          // `createdBy` peut désigner un établissement : le type du compte
          // est donc contrôlé avant de l'afficher comme professeur.
          if (userData.userType === "student") {
            const classroomTeacher =
              userData.classe?.teacherId ||
              userData.classe?.profId ||
              userData.classe?.teacher ||
              userData.classe?.professeur;
            const classroomTeacherId =
              typeof classroomTeacher === "string"
                ? classroomTeacher
                : classroomTeacher?.uid || classroomTeacher?.id;
            const teacherIds = Array.from(
              new Set(
                [userData.teacherId, classroomTeacherId, userData.createdBy]
                  .filter(Boolean),
              ),
            ) as string[];

            const teachers = (
              await Promise.all(
                teacherIds.map(async (teacherId) => {
                  const teacherSnap = await getDoc(doc(db, "users", teacherId));
                  if (!teacherSnap.exists()) return null;

                  const teacher = {
                    uid: teacherSnap.id,
                    ...teacherSnap.data(),
                  } as User;
                  return ["teacher", "independentTeacher"].includes(
                    teacher.userType,
                  )
                    ? teacher
                    : null;
                }),
              )
            ).filter(Boolean) as User[];

            userData.professeurs = teachers;
            userData.professeur = teachers[0] || null;
          }

          // Récupérer le parent de l'utilisateur 
          if (userData.parentId) {
            const parentRef = doc(db, "users", userData.parentId);
            const parentSnap = await getDoc(parentRef);

            if (parentSnap.exists()) {
              userData.parent = {
                uid: parentSnap.id,
                ...parentSnap.data(),
              };
            }
          }



          return { data: userData };
        } catch (error: any) {
          return { error: { status: 500, message: error.message } };
        }
      },
      providesTags: ["userId"],
    }),
    // Supprimer
    deleteUser: builder.mutation({
      queryFn: async (uid) => {
        try {
          const docRef = doc(db, "users", uid);
          await deleteDoc(docRef);
          return {
            data: { uid, message: "Utilisateur supprimé." },
          };
        } catch (error: any) {
          return { error: { status: "DELETE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users"],
    }),

    manageUser: builder.mutation({
      queryFn: async ({ userId, action }) => {
        try {
          const data = await manageUser(userId, action);
          return { data };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ["admins", "users"],
    }),
    getAllUsers: builder.query({
      async queryFn({ userType }) {
        try {
          return new Promise((resolve) => {
            const usersRef = collection(db, "users");
            const q = userType
              ? query(usersRef, where("userType", "==", userType))
              : query(usersRef);

            onSnapshot(q, (snapshot) => {
              const users = snapshot.docs.map((snap) => ({
                uid: snap.id,
                ...snap.data(),
              }));
              resolve({ data: users });
            });
          });
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        { userType },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const usersRef = collection(db, "users");
        const q = userType
          ? query(usersRef, where("userType", "==", userType))
          : query(usersRef);

        let unsubscribe;
        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(q, (snapshot) => {
            const users = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            }));
            updateCachedData(() => users);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["users"],
    }),
    importStudent: builder.mutation({
      async queryFn(rows: Record<string, unknown>[]) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }

          const response = await importStudents(rows);

          return { data: response };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "students"],
    }),
    importTeacher: builder.mutation({
      async queryFn(rows: Record<string, unknown>[]) {
        try {
          if (!auth.currentUser) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }

          const response = await importTeachers(rows);
          return { data: response };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["profs", "users"],
    }),
    getStudentsOfParent: builder.query({
      queryFn: async (userId) => {
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          where("parentId", "==", userId),
          where("userType", "==", "student"),
          orderBy("createdAt", "desc"),
        );

        const snapshot = await getDocs(q);

        const users = await enrichStudentsWithClasseAndProf(snapshot.docs);

        return { data: users as unknown as User[] };
      },
      // Gestion du temps réel avec Firestore et `onSnapshot`
      async onCacheEntryAdded(
        userId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe;

        try {
          await cacheDataLoaded;

          const usersRef = collection(db, "users");

          const q = query(
            usersRef,
            where("parentId", "==", userId),
            where("userType", "==", "student"),
            orderBy("createdAt", "desc"),
          );

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const users = await enrichStudentsWithClasseAndProf(snapshot.docs);

            // Met à jour le cache RTK Query avec les nouvelles données
            updateCachedData(() => users as unknown as User[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["users"],
    }),
    getStudentsOfProf: builder.query({
      queryFn: async (userId) => {
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          where("createdBy", "==", userId),
          where("userType", "==", "student"),
          orderBy("createdAt", "desc"),
        );

        const snapshot = await getDocs(q);

        const users = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const user = docSnap.data();

            return {
              id: docSnap.id,
              ...user,
            };
          }),
        );

        return { data: users as unknown as User[] };
      },
      // Gestion du temps réel avec Firestore et `onSnapshot`
      async onCacheEntryAdded(
        userId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe;

        try {
          await cacheDataLoaded;

          const usersRef = collection(db, "users");

          const q = query(
            usersRef,
            where("createdBy", "==", userId),
            where("userType", "==", "student"),
            orderBy("createdAt", "desc"),
          );

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const users = await Promise.all(
              snapshot.docs.map(async (docSnap) => {
                const user = docSnap.data();

                return {
                  id: docSnap.id,
                  ...user,
                };
              }),
            );

            // Met à jour le cache RTK Query avec les nouvelles données
            updateCachedData(() => users as unknown as User[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["users"],
    }),
    getParentsByProf: builder.query({
      queryFn: async (userId) => {
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          where("createdBy", "==", userId),
          where("userType", "==", "parent"),
          orderBy("createdAt", "desc"),
        );

        const snapshot = await getDocs(q);

        const users = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const user = docSnap.data();

            return {
              id: docSnap.id,
              ...user,
            };
          }),
        );

        return { data: users as unknown as User[] };
      },
      // Gestion du temps réel avec Firestore et `onSnapshot`
      async onCacheEntryAdded(
        userId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe;

        try {
          await cacheDataLoaded;

          const usersRef = collection(db, "users");

          const q = query(
            usersRef,
            where("createdBy", "==", userId),
            where("userType", "==", "parent"),
            orderBy("createdAt", "desc"),
          );

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const users = await Promise.all(
              snapshot.docs.map(async (docSnap) => {
                const user = docSnap.data();

                return {
                  id: docSnap.id,
                  ...user,
                };
              }),
            );

            // Met à jour le cache RTK Query avec les nouvelles données
            updateCachedData(() => users as unknown as User[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["users"],
    }),
    getDashboardStudent: builder.query({
      async queryFn(userId: string) {
        try {
          if (!userId) {
            return {
              data: {
                matieresCount: 0,
                evaluationCount: 0,
                absencesCount: 0,
                objectifsEnCours: [],
                devoirsAFaire: [],
                competencesGlobal: [],
                observation: null,
              },
            };
          }

          const studentId = userId;

          const now = new Date();
          const startOfWeek = new Timestamp(
            Math.floor(
              new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() - now.getDay(),
              ).getTime() / 1000,
            ),
            0,
          );
          const endOfWeek = new Timestamp(
            Math.floor(
              new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() - now.getDay() + 7,
              ).getTime() / 1000,
            ),
            0,
          );

          const q = query(
            collection(db, "evaluations"),
            where("student", "==", studentId),
            orderBy("createdAt", "desc")
          );

          const [
            competencesSnap,
            devoirsStudentSnap,
            absencesSnap,
            objectifsStudentSnap,
            evaluationsSnap,
            observationSnap,
          ] = await Promise.all([
            getDocs(
              query(
                collection(db, "competencesStudents"),
                where("studentId", "==", studentId),
              ),
            ),
            getDocs(
              query(
                collection(db, "devoirStudents"),
                where("studentId", "==", studentId),
              ),
            ),
            getDocs(
              query(
                collection(db, "absences"),
                where("student.uid", "==", studentId),
              ),
            ),
            getDocs(
              query(
                collection(db, "objectifStudents"),
                where("studentId", "==", studentId),
              ),
            ),
            getDocs(
              query(
                collection(db, "evaluations"),
                where("student", "==", studentId),
              ),
            ),
            getDoc(doc(db, "observations", studentId)),
          ]);

          const competencesStudents = competencesSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) as CompetenceStudent[];

          const devoirStudents = devoirsStudentSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) as DevoirStudent[];

          const absences = absencesSnap.docs.map((docSnap) => ({
            uid: docSnap.id,
            ...docSnap.data(),
          })) as Absence[];

          const objectifStudents = objectifsStudentSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) as ObjectifStudent[];

          const evaluations = evaluationsSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) as Evaluation[];

          const observation = observationSnap.exists()
            ? ({ id: observationSnap.id, ...observationSnap.data() } as Observation)
            : null;

          const uniqueMatieres = Array.from(
            new Set(competencesStudents.map((c) => c.matiereId)),
          );

          const devoirsAFaireRaw = devoirStudents.filter(
            (d) => d.status === "Non fait" || d.status === "En retard",
          );

          const devoirsAFaire = await Promise.all(
            devoirsAFaireRaw.map(async (d) => {
              const devoirSnap = await getDoc(doc(db, "devoirs", d.devoirId));
              return {
                ...d,
                devoirData: devoirSnap.exists()
                  ? (devoirSnap.data() as Devoir)
                  : null,
              };
            }),
          );

          const objectifsWithData = await Promise.all(
            objectifStudents.map(async (o) => {
              const [objectifSnap, professorSnap] = await Promise.all([
                getDoc(doc(db, "objectifs", o.objectifId)),
                getDoc(doc(db, "users", o.createdBy)),
              ]);
              return {
                ...o,
                objectifData: objectifSnap.exists()
                  ? (objectifSnap.data() as Objectif)
                  : null,
                professorData: professorSnap.exists()
                  ? (professorSnap.data() as User)
                  : null,
              };
            }),
          );

          const objectifsEnCours = objectifsWithData.filter((o) => {
            return (o.status === "En cours" || o.status === "Nouveau");
          });

          const competencesWithData = await Promise.all(
            competencesStudents.map(async (competence) => {
              if (typeof competence.competenceId !== "string") {
                const matiereSnap = await getDoc(
                  doc(db, "matieres", competence.matiereId),
                );
                return {
                  ...competence,
                  competenceData: null,
                  matiereData: matiereSnap.exists()
                    ? (matiereSnap.data() as Matiere)
                    : null,
                };
              }

              const [competenceSnap, matiereSnap] = await Promise.all([
                getDoc(doc(db, "competences", competence.competenceId)),
                getDoc(doc(db, "matieres", competence.matiereId)),
              ]);

              return {
                ...competence,
                competenceData: competenceSnap.exists()
                  ? (competenceSnap.data() as Competence)
                  : null,
                matiereData: matiereSnap.exists()
                  ? (matiereSnap.data() as Matiere)
                  : null,
              };
            }),
          );

          return {
            data: {
              matieresCount: uniqueMatieres.length,
              evaluationCount: evaluations.length,
              // devoirsFaitsCount: devoirsFaits,
              absencesCount: absences.length,
              // objectifsDeLaSemaine,
              objectifsEnCours,
              devoirsAFaire,
              competencesGlobal: competencesWithData,
              observation,
            },
          };
        } catch (error) {
          console.error("Erreur récupération dashboard étudiant:", error);
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },

      keepUnusedDataFor: 0,

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        if (!arg) return;

        const user = auth.currentUser;
        if (!user) return;

        const studentId = arg;

        const devoirsRef = query(
          collection(db, "devoirStudents"),
          where("studentId", "==", studentId),
        );
        const observationRef = doc(db, "observations", studentId);

        let unsubscribe: (() => void) | undefined;
        let unsubscribeObservation: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(devoirsRef, async () => {
            try {
              const now = new Date();
              const startOfWeek = new Timestamp(
                Math.floor(
                  new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate() - now.getDay(),
                  ).getTime() / 1000,
                ),
                0,
              );
              const endOfWeek = new Timestamp(
                Math.floor(
                  new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate() - now.getDay() + 7,
                  ).getTime() / 1000,
                ),
                0,
              );

              const [
                competencesSnap,
                devoirsStudentSnap,
                absencesSnap,
                objectifsStudentSnap,
                evaluationsSnap,
                observationSnap,
              ] = await Promise.all([
                getDocs(
                  query(
                    collection(db, "competencesStudents"),
                    where("studentId", "==", studentId),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "devoirStudents"),
                    where("studentId", "==", studentId),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "absences"),
                    where("student.uid", "==", studentId),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "objectifStudents"),
                    where("studentId", "==", studentId),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "evaluations"),
                    where("student", "==", studentId),
                  ),
                ),
                getDoc(doc(db, "observations", studentId)),
              ]);

              const competencesStudents = competencesSnap.docs.map(
                (docSnap) => ({ id: docSnap.id, ...docSnap.data() }),
              ) as CompetenceStudent[];
              const devoirStudents = devoirsStudentSnap.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
              })) as DevoirStudent[];
              const absences = absencesSnap.docs.map((docSnap) => ({
                uid: docSnap.id,
                ...docSnap.data(),
              })) as Absence[];
              const objectifStudents = objectifsStudentSnap.docs.map(
                (docSnap) => ({ id: docSnap.id, ...docSnap.data() }),
              ) as ObjectifStudent[];

              const evaluations = evaluationsSnap.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
              })) as Evaluation[];

              const observation = observationSnap.exists()
                ? ({ id: observationSnap.id, ...observationSnap.data() } as Observation)
                : null;

              const uniqueMatieres = Array.from(
                new Set(competencesStudents.map((c) => c.matiereId)),
              );
              // const devoirsFaits = devoirStudents.filter(
              //   (d) => d.status === "Fait",
              // ).length;

              const devoirsAFaireRaw = devoirStudents.filter(
                (d) => d.status === "Non fait" || d.status === "En retard",
              );

              const devoirsAFaire = await Promise.all(
                devoirsAFaireRaw.map(async (d) => {
                  const devoirSnap = await getDoc(
                    doc(db, "devoirs", d.devoirId),
                  );
                  return {
                    ...d,
                    devoirData: devoirSnap.exists()
                      ? (devoirSnap.data() as Devoir)
                      : null,
                  };
                }),
              );

              const objectifsWithData = await Promise.all(
                objectifStudents.map(async (o) => {
                  const [objectifSnap, professorSnap] = await Promise.all([
                    getDoc(doc(db, "objectifs", o.objectifId)),
                    getDoc(doc(db, "users", o.createdBy)),
                  ]);
                  return {
                    ...o,
                    objectifData: objectifSnap.exists()
                      ? (objectifSnap.data() as Objectif)
                      : null,
                    professorData: professorSnap.exists()
                      ? (professorSnap.data() as User)
                      : null,
                  };
                }),
              );

              // const objectifsDeLaSemaine = objectifsWithData.filter((o) => {
              //   const endDate =
              //     o.objectifData?.endDate instanceof Timestamp
              //       ? o.objectifData.endDate
              //       : null;
              //   return (
              //     endDate &&
              //     endDate.seconds >= startOfWeek.seconds &&
              //     endDate.seconds < endOfWeek.seconds
              //   );
              // });

              const objectifsEnCours = objectifsWithData.filter((o) => {
                return (o.status === "En cours" || o.status === "Nouveau");
              });

              const competencesWithData = await Promise.all(
                competencesStudents.map(async (competence) => {
                  if (typeof competence.competenceId !== "string") {
                    const matiereSnap = await getDoc(
                      doc(db, "matieres", competence.matiereId),
                    );
                    return {
                      ...competence,
                      competenceData: null,
                      matiereData: matiereSnap.exists()
                        ? (matiereSnap.data() as Matiere)
                        : null,
                    };
                  }
                  const [competenceSnap, matiereSnap] = await Promise.all([
                    getDoc(doc(db, "competences", competence.competenceId)),
                    getDoc(doc(db, "matieres", competence.matiereId)),
                  ]);
                  return {
                    ...competence,
                    competenceData: competenceSnap.exists()
                      ? (competenceSnap.data() as Competence)
                      : null,
                    matiereData: matiereSnap.exists()
                      ? (matiereSnap.data() as Matiere)
                      : null,
                  };
                }),
              );

              updateCachedData(() => ({
                matieresCount: uniqueMatieres.length,
                evaluationCount: evaluations.length,
                // devoirsFaitsCount: devoirsFaits,
                absencesCount: absences.length,
                // objectifsDeLaSemaine,
                objectifsEnCours,
                devoirsAFaire,
                competencesGlobal: competencesWithData,
                observation,
              }));
            } catch (err) {
              console.error("Erreur mise à jour dashboard étudiant:", err);
            }
          });
          unsubscribeObservation = onSnapshot(observationRef, (snapshot) => {
            const observation = snapshot.exists()
              ? ({ id: snapshot.id, ...snapshot.data() } as Observation)
              : null;
            updateCachedData((dashboard) => {
              if (dashboard) dashboard.observation = observation;
            });
          });
        } catch {
          return;
        }

        await cacheEntryRemoved;
        unsubscribe?.();
        unsubscribeObservation?.();
      },

      providesTags: ["dashboardStudent"],
    }),
    getArchiveStudentsByProf: builder.query({
      async queryFn() {
        const user = auth.currentUser;
        const profId = user?.uid;

        if (!user) {
          return { error: { message: "Aucun utilisateur connecté." } };
        }

        try {
          return new Promise((resolve, reject) => {
            const q = query(
              collection(db, "users"),
              where("userType", "==", "student"),
              where("createdBy", "==", profId),
              where("archived", "==", true),
            );

            const unsubscribe = onSnapshot(
              q,
              async (snapshot) => {
                const students = snapshot.docs.map((snap) => ({
                  uid: snap.id,
                  ...snap.data(),
                })) as any[];

                // Récupérer les objets classroom pour chaque student
                const studentsWithClassroom = await Promise.all(
                  students.map(async (student) => {
                    if (!student.classe) return student; // pas de classe assignée

                    try {
                      const classroomRef = doc(
                        db,
                        "classrooms",
                        student.classe,
                      ); // adapte le nom de ta collection
                      const classroomSnap = await getDoc(classroomRef);

                      return {
                        ...student,
                        classe: classroomSnap.exists()
                          ? { uid: classroomSnap.id, ...classroomSnap.data() }
                          : student.classe, // fallback à l'id si non trouvé
                      };
                    } catch {
                      return student; // fallback en cas d'erreur
                    }
                  }),
                );

                // Récupérer le parent pour chaque student
                const studentsWithParent = await Promise.all(
                  studentsWithClassroom.map(async (student) => {
                    if (!student.parentId) return student; // pas de classe assignée

                    try {
                      const userRef = doc(db, "users", student.parentId); // adapte le nom de ta collection
                      const userSnap = await getDoc(userRef);

                      return {
                        ...student,
                        parent: userSnap.exists()
                          ? { uid: userSnap.id, ...userSnap.data() }
                          : student.parentId, // fallback à l'id si non trouvé
                      };
                    } catch {
                      return student; // fallback en cas d'erreur
                    }
                  }),
                );

                resolve({ data: studentsWithParent });
                unsubscribe();
              },
              reject,
            );
          });
        } catch (error) {
          return { error };
        }
      },

      keepUnusedDataFor: 0,

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const user = auth.currentUser;
        const profId = user?.uid;

        const usersRef = query(
          collection(db, "users"),
          where("userType", "==", "student"),
          where("createdBy", "==", profId),
          where("archived", "==", true),
        );

        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(usersRef, async (snapshot) => {
            const students = snapshot.docs.map((snap) => ({
              uid: snap.id,
              ...snap.data(),
            })) as any[];

            //Même logique de jointure dans onCacheEntryAdded
            const studentsWithClassroom = await Promise.all(
              students.map(async (student) => {
                if (!student.classe) return student;

                try {
                  const classroomRef = doc(db, "classrooms", student.classe);
                  const classroomSnap = await getDoc(classroomRef);

                  return {
                    ...student,
                    classe: classroomSnap.exists()
                      ? { uid: classroomSnap.id, ...classroomSnap.data() }
                      : student.classe,
                  };
                } catch {
                  return student;
                }
              }),
            );

            // Récupérer le parent pour chaque student
            const studentsWithParent = await Promise.all(
              studentsWithClassroom.map(async (student) => {
                if (!student.parentId) return student; // pas de classe assignée

                try {
                  const userRef = doc(db, "users", student.parentId); // adapte le nom de ta collection
                  const userSnap = await getDoc(userRef);

                  return {
                    ...student,
                    parent: userSnap.exists()
                      ? { uid: userSnap.id, ...userSnap.data() }
                      : student.parentId, // fallback à l'id si non trouvé
                  };
                } catch {
                  return student; // fallback en cas d'erreur
                }
              }),
            );

            updateCachedData(() => studentsWithParent);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },

      providesTags: ["students"],
    }),
    archiveUser: builder.mutation({
      queryFn: async (userId) => {
        try {
          await updateDoc(doc(db, "users", userId), {
            archived: true,
            archivedAt: serverTimestamp(),
          });
          return {
            data: { userId, message: "Utilisateur archivé !", success: true },
          };
        } catch (error: any) {
          return { error: { status: "ARCHIVE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "profs", "students"],
    }),
    removeStudentParent: builder.mutation({
      // Retire uniquement le lien côté élève (parentId -> null). Ne touche
      // jamais au compte parent lui-même (pas de suppression Auth)
      queryFn: async (studentId: string) => {
        try {
          await updateDoc(doc(db, "users", studentId), {
            parentId: null,
          });
          return {
            data: { studentId, message: "Tuteur retiré !", success: true },
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "userId", "students"],
    }),
    unArchiveUser: builder.mutation({
      queryFn: async (userId) => {
        try {
          await updateDoc(doc(db, "users", userId), {
            archived: false,
            archivedAt: serverTimestamp(),
          });
          return {
            data: { userId, message: "Utilisateur désarchivé !", success: true },
          };
        } catch (error: any) {
          return { error: { status: "ARCHIVE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["admins", "users", "profs", "students"],
    }),
    toggleMatiere: builder.mutation({
      queryFn: async ({ matiereId, enabledMatieres }) => {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }
          const userId = user.uid;
          const isCurrentlyEnabled = enabledMatieres?.includes(matiereId);
          await updateDoc(doc(db, "users", userId), {
            enabledMatieres: isCurrentlyEnabled
              ? arrayRemove(matiereId) : arrayUnion(matiereId)
          });
          return {
            data: { userId, message: `Matière ${isCurrentlyEnabled ? "archivée" : "désarchivée"}`, success: true, matiereId, enabled: !isCurrentlyEnabled },
          };
        } catch (error: any) {
          return { error: { status: "ARCHIVE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["user"],
    })
  }),
});

export const {
  useGetCurrentUserQuery,
  useUpdateUserInfoMutation,
  useUpdateUserPhotoURLMutation,
  useAddOrEditUserMutation,
  useGetAdminsQuery,
  useGetProfsQuery,
  useGetArchiveProfsQuery,
  useDeleteUserMutation,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useGetAllUsersQuery,
  useManageUserMutation,
  useAddOrEditStudentAndParentMutation,
  useChangeStudentTutorMutation,
  useGetStudentsByProfQuery,
  useGetStudentsAndParentsByProfQuery,
  useGetProfsByStudentQuery,
  useImportStudentMutation,
  useImportTeacherMutation,
  useResendAccessMutation,
  useGetStudentsOfProfQuery,
  useGetDashboardStudentQuery,
  useArchiveUserMutation,
  useUnArchiveUserMutation,
  useRemoveStudentParentMutation,
  useGetArchiveStudentsByProfQuery,
  useGetParentsByProfQuery,
  useLazyGetParentsByProfQuery,
  useGetStudentsOfParentQuery,
  useToggleMatiereMutation
} = UserApi;

export function useUserFromLocation(): [
  User & { bio: string },
  boolean,
  string,
  (id: string) => any,
] {
  const itemState = useLocationState<User | undefined>(undefined);
  const [item, setItem] = useState(itemState);
  const { id } = useParams<{ id: string }>();
  const [findById, { data, isError, isLoading }] = useLazyGetUserByIdQuery();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      findById(id as string);
    }
  }, [id]);
  useEffect(() => {
    if (isError && !itemState) {
      navigate(-1);
    }
  }, [isError]);
  useEffect(() => {
    if (data) {
      setItem(data as User);
    }
  }, [data]);

  return [item as User & { bio: string }, isLoading, id as string, findById];
}


const enrichStudentsWithClasseAndProf = async (
  docs: QueryDocumentSnapshot<DocumentData>[],
) => {
  return Promise.all(
    docs.map(async (docSnap) => {
      const user = docSnap.data();

      const [classeSnap, profSnap] = await Promise.all([
        user.classe ? getDoc(doc(db, "classrooms", user.classe)) : null,
        user.createdBy ? getDoc(doc(db, "users", user.createdBy)) : null,
      ]);

      return {
        id: docSnap.id,
        ...user,
        classeData:
          classeSnap && classeSnap.exists()
            ? { id: classeSnap.id, ...classeSnap.data() }
            : null,
        professeur:
          profSnap && profSnap.exists()
            ? { id: profSnap.id, ...profSnap.data() }
            : null,
      };
    }),
  );
};
